import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { TALK_NORMAL_ROOMS, TALK_PREMIUM_ROOMS } from '../data/talkRoomsConfig';
import { RoomListGlyph, IconChevronRight, IconClose, IconFlag } from '../components/talkAppleIcons';
import { useAuth } from '../context/AuthContext';
import ReportModal from '../components/ReportModal';

const PREMIUM_ANIM_ID = 'talk-rooms-premium-anim';
const PREMIUM_ANIM_CSS = `
@keyframes talkPremiumGlow {
  0%, 100% { box-shadow: 0 0 18px rgba(201,168,76,0.28), inset 0 1px 0 rgba(255,255,255,0.1); }
  50% { box-shadow: 0 0 32px rgba(232,208,138,0.5), inset 0 1px 0 rgba(255,255,255,0.18); }
}
@keyframes talkPremiumBadgePulse {
  0%, 100% { box-shadow: 0 0 14px rgba(201,168,76,0.35); }
  50% { box-shadow: 0 0 26px rgba(232,208,138,0.65); }
}
.talk-premium-card { animation: talkPremiumGlow 3.2s ease-in-out infinite; }
.talk-premium-lux-card { animation: talkPremiumGlow 2.6s ease-in-out infinite; }
.talk-premium-badge { animation: talkPremiumBadgePulse 2.4s ease-in-out infinite; }
`;

function hasTalkPremium(user) {
  return !!user && Number(user.talk_premium) === 1;
}

export default function TalkRooms() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const premium = hasTalkPremium(user);
  const [banner, setBanner] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');
  const [customRooms, setCustomRooms] = useState([]);
  // Guideline 1.2: 他人が作ったルームの通報
  const [reportRoom, setReportRoom] = useState(null);

  useEffect(() => {
    axios.get('/api/rooms').then(r => setCustomRooms(r.data)).catch(() => {});
  }, []);

  const submitCreate = async () => {
    if (!createTitle.trim()) return;
    setCreateBusy(true);
    setCreateError('');
    try {
      const { data } = await axios.post('/api/rooms', { title: createTitle, description: createDesc });
      setCustomRooms(prev => [data, ...prev]);
      setShowCreate(false);
      setCreateTitle('');
      setCreateDesc('');
    } catch (e) {
      setCreateError(e?.response?.data?.error || '作成に失敗しました');
    } finally {
      setCreateBusy(false);
    }
  };

  const deleteCustomRoom = async (id) => {
    await axios.delete(`/api/rooms/${id}`).catch(() => {});
    setCustomRooms(prev => prev.filter(r => r.id !== id));
  };

  useEffect(() => {
    const msg = location.state?.talkDeniedReason;
    if (typeof msg === 'string' && msg.trim()) {
      setBanner(msg.trim());
      navigate('/talk', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!premium) return undefined;
    if (document.getElementById(PREMIUM_ANIM_ID)) return undefined;
    const style = document.createElement('style');
    style.id = PREMIUM_ANIM_ID;
    style.textContent = PREMIUM_ANIM_CSS;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(PREMIUM_ANIM_ID);
      if (el) el.remove();
    };
  }, [premium]);

  const openRoom = (id) => {
    navigate(`/talk/${id}`);
  };


  return (
    <div data-control-theme="dark" style={{ ...S.page, ...(premium ? S.pagePremium : {}) }}>
      <header style={{ ...S.header, ...(premium ? S.headerPremium : {}) }}>
        <div style={S.headerRow}>
          <div style={S.headerMain}>
            <div style={S.headerInner}>
              <span style={{ ...S.headerAccent, ...(premium ? S.headerAccentLux : {}) }} aria-hidden>✦</span>
              <h1 style={{ ...S.title, ...(premium ? S.titleLux : {}) }}>JTALK</h1>
            </div>
          </div>
          {!premium ? (
            <button data-control="secondary" data-control-id="TalkRooms-headerPremiumBtn"
              type="button"
              style={S.headerPremiumBtn}
              onClick={() => navigate('/talk/premium')}
              aria-label="プレミアムに課金"
            >
              
              <span style={S.headerPremiumText}>
                <span style={S.headerPremiumLabel}>プレミアム</span>
                
              </span>
            </button>
          ) : (
            <span className="talk-premium-badge" style={S.headerPremiumActiveLux} aria-label="プレミアム有効">
              <span style={S.headerPremiumActiveDot} aria-hidden />
              <span style={S.headerPremiumActiveStar} aria-hidden>✦</span>
              Premium
            </span>
          )}
        </div>
      </header>

      {banner ? (
        <div style={S.banner} role="status">
          <div style={S.bannerBody}>
            <span>{banner}</span>
            {!premium ? (
              <button data-control="text" data-control-id="TalkRooms-bannerCta"
                type="button"
                style={S.bannerCta}
                onClick={() => navigate('/talk/premium')}
              >
                クレジットカードでプレミアムへ
              </button>
            ) : null}
          </div>
          <button data-control="icon" data-control-id="TalkRooms-bannerClose" type="button" style={S.bannerClose} onClick={() => setBanner('')} aria-label="閉じる"><IconClose /></button>
        </div>
      ) : null}

      <div style={{ ...S.divider, ...(premium ? S.dividerLux : {}) }} />

      <div style={S.sectionRow}>
        <h2 style={{ ...S.sectionLabel, margin: 0 }}>
          <span style={S.sectionDot} aria-hidden />
          ルーム一覧
        </h2>
      </div>
      <div style={S.hintRow}>
        <button data-control="secondary" data-control-id="TalkRooms-createBtn"
          type="button"
          style={S.createBtn}
          onClick={() => { setShowCreate(true); setCreateError(''); }}
          aria-label="ルームを作成"
        >ルームを作成</button>
      </div>
      <div style={S.list}>
        {[...TALK_NORMAL_ROOMS, ...TALK_PREMIUM_ROOMS].map((r) => (
          <button data-control="row" data-control-id="TalkRooms-card"
            key={r.id}
            type="button"
            style={{ ...S.card, background: r.gradient }}
            onClick={() => openRoom(r.id)}
          >
            <div style={S.hostBlock}>
              <div style={S.officialAvatar}>
                <RoomListGlyph name={r.glyph} size={24} />
              </div>
              <span data-control-copy="caption" style={S.hostBlockName}>{r.host?.name || 'JTALK'}</span>
            </div>
            <div style={S.cardBody}>
              <span style={S.cardTitle}>{r.title}</span>
              <span data-control-copy="caption" style={S.cardDesc}>{r.desc}</span>
            </div>
            <span style={S.chevron} aria-hidden>
              <IconChevronRight size={16} />
            </span>
          </button>
        ))}
      </div>

      {customRooms.length > 0 && (
        <>
          <h2 style={{ ...S.sectionLabel, marginTop: 28 }}>
            <span style={S.sectionDot} aria-hidden />
            作成したルーム
          </h2>
          <div style={S.list}>
            {customRooms.map(r => (
              <div key={r.id} style={{ position: 'relative' }}>
                <button data-control="row" data-control-id="TalkRooms-card" className="jt-custom-room" type="button" style={S.card} onClick={() => openRoom(r.id)}>
                <div style={S.hostBlock}>
                  {r.owner_avatar
                    ? <img src={r.owner_avatar} alt="" style={S.userAvatar} />
                    : <span style={S.userAvatarFallback}>{(r.owner_name || '?')[0].toUpperCase()}</span>
                  }
                  <span data-control-copy="caption" style={S.hostBlockName}>{r.owner_name}</span>
                </div>
                <div style={S.cardBody}>
                  <span style={S.cardTitle}>{r.title}</span>
                  {r.description ? <span data-control-copy="caption" style={S.cardDesc}>{r.description}</span> : null}
                </div>
                </button>
                {r.owner_id === user?.id ? (
                  <button data-control="icon" data-control-id="TalkRooms-deleteRoomBtn"
                    style={S.deleteRoomBtn}
                    onClick={e => { e.stopPropagation(); deleteCustomRoom(r.id); }}
                    aria-label="削除"
                  ><IconClose /></button>
                ) : (
                  <button data-control="icon" data-control-id="TalkRooms-reportRoomBtn"
                    style={S.reportRoomBtn}
                    onClick={e => { e.stopPropagation(); setReportRoom(r); }}
                    aria-label={`${r.title} を通報`}
                    title="通報する"
                  ><IconFlag /></button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {showCreate && (
        <div style={S.modalBackdrop} onClick={() => setShowCreate(false)}>
          <div style={S.modal} data-control-theme="light" onClick={e => e.stopPropagation()}>
            {/* ヘッダー */}
            <div style={S.modalHeader}>
              <button data-control="quiet" data-control-id="TalkRooms-modalCancelBtn" style={S.modalCancelBtn} onClick={() => setShowCreate(false)}>キャンセル</button>
              <span style={S.modalTitle}>新しいルーム</span>
              <button data-control="primary" data-control-id="TalkRooms-modalPostBtn"
                style={{ ...S.modalPostBtn, color: createBusy || !createTitle.trim() ? '#a5a5a5' : '#1674c4' }}
                onClick={submitCreate}
                disabled={createBusy || !createTitle.trim()}
              >
                {createBusy ? '作成中…' : '作成'}
              </button>
            </div>

            {/* アイコン＋入力エリア（Twitterコンポーズ風） */}
            <div style={S.modalCompose}>
              
              <div style={S.modalFields}>
                <input
                  style={S.modalTitleInput}
                  aria-label="ルーム名"
                  placeholder="ルーム名"
                  value={createTitle}
                  onChange={e => setCreateTitle(e.target.value)}
                  maxLength={40}
                  autoFocus
                />
                <div style={S.modalDividerLine} />
                <input
                  style={S.modalDescInput}
                  aria-label="ルームの説明（任意）"
                  placeholder="どんな話をするルームですか？（任意）"
                  value={createDesc}
                  onChange={e => setCreateDesc(e.target.value)}
                  maxLength={100}
                />
              </div>
            </div>

            {createError ? <p style={S.modalError}>{createError}</p> : null}

            {/* 文字数カウンター */}
            <div style={S.modalFooter}>
              <span style={S.modalCount}>{createTitle.length}/40</span>
            </div>
          </div>
        </div>
      )}

      {reportRoom && (
        <ReportModal
          targetType="room"
          targetId={reportRoom.id}
          targetName={`${reportRoom.title}（作成者: ${reportRoom.owner_name}）`}
          onClose={() => setReportRoom(null)}
          onReported={({ targetId }) => {
            setCustomRooms(rs => rs.filter(x => String(x.id) !== String(targetId)));
          }}
        />
      )}

      <p style={S.hint}>
        ✦ {premium
          ? 'プレミアム会員は時間制限なしでルームをお楽しみください。'
          : 'ライブルームは無料で1時間まで利用できます。プレミアムで無制限に。'} 公共の場ではミュートやイヤホンをご利用ください。
      </p>
    </div>
  );
}

const GOLD = '#c9a84c';
const GOLD2 = '#e8d08a';
const BG = '#07070f';
const SURFACE = 'rgba(255,255,255,0.035)';
const BORDER_GOLD = 'rgba(201,168,76,0.2)';
const TEXT = '#f0ece0';
const TEXT2 = 'rgba(240,236,224,0.55)';
const TEXT3 = 'rgba(240,236,224,0.32)';

const S = {
  page: {
    background: BG,
    minHeight: '100vh',
    paddingBottom: 100,
    fontFamily: '"SF Pro Display", "Hiragino Sans", "Helvetica Neue", sans-serif',
  },
  pagePremium: {
    background: `
      radial-gradient(ellipse 90% 55% at 50% -8%, rgba(201,168,76,0.18) 0%, transparent 55%),
      radial-gradient(ellipse 55% 40% at 95% 15%, rgba(160,120,200,0.14) 0%, transparent 50%),
      radial-gradient(ellipse 45% 35% at 5% 75%, rgba(201,168,76,0.1) 0%, transparent 45%),
      ${BG}
    `,
  },
  banner: {
    margin: '12px 16px 0',
    padding: '12px 14px',
    borderRadius: 12,
    background: 'rgba(180,80,80,0.2)',
    border: '1px solid rgba(220,120,120,0.35)',
    color: TEXT,
    fontSize: 13,
    lineHeight: 1.45,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  bannerBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  bannerCta: {
    alignSelf: 'flex-start',
    padding: '8px 12px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'inherit',
    color: '#fff',
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  },
  bannerClose: {
    flexShrink: 0,
    background: 'none',
    border: 'none',
    color: TEXT2,
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    width: 28,
    height: 28,
  },
  header: {
    padding: '22px 20px 14px',
    background: 'linear-gradient(180deg, rgba(201,168,76,0.08) 0%, transparent 100%)',
    borderBottom: `1px solid ${BORDER_GOLD}`,
    position: 'sticky', top: 0, zIndex: 10,
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  },
  headerPremium: {
    background: 'linear-gradient(180deg, rgba(201,168,76,0.2) 0%, rgba(160,120,200,0.1) 55%, transparent 100%)',
    borderBottom: '1px solid rgba(232,208,138,0.35)',
    boxShadow: '0 4px 24px rgba(201,168,76,0.12)',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  headerMain: {
    flex: 1,
    minWidth: 0,
  },
  headerInner: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  headerPremiumBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 14,
    border: '1px solid rgba(160,120,200,0.45)',
    background: 'linear-gradient(135deg, rgba(123,92,184,0.35) 0%, rgba(90,61,138,0.25) 100%)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(90,61,138,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
    textAlign: 'left',
  },
  headerPremiumBadge: {
    fontSize: 16,
    lineHeight: 1,
    background: `linear-gradient(135deg, ${GOLD2}, ${GOLD})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  headerPremiumText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    alignItems: 'flex-start',
  },
  headerPremiumLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: '#e8d08a',
    letterSpacing: '0.02em',
    lineHeight: 1.2,
  },
  headerPremiumSub: {
    fontSize: 10,
    fontWeight: 700,
    color: 'rgba(230,210,255,0.9)',
    letterSpacing: '0.04em',
    lineHeight: 1.2,
  },
  headerPremiumActive: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderRadius: 999,
    border: `1px solid ${BORDER_GOLD}`,
    background: 'rgba(201,168,76,0.12)',
    fontSize: 11,
    fontWeight: 800,
    color: GOLD2,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  headerPremiumActiveLux: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '9px 14px',
    borderRadius: 999,
    border: '1px solid rgba(232,208,138,0.55)',
    background: 'linear-gradient(135deg, rgba(201,168,76,0.32) 0%, rgba(160,120,200,0.2) 100%)',
    fontSize: 11,
    fontWeight: 800,
    color: GOLD2,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    textShadow: '0 0 12px rgba(232,208,138,0.4)',
  },
  headerPremiumActiveStar: {
    fontSize: 10,
    lineHeight: 1,
    color: GOLD2,
    filter: 'drop-shadow(0 0 4px rgba(232,208,138,0.8))',
  },
  headerPremiumActiveDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#9d7',
    boxShadow: '0 0 8px rgba(153,221,119,0.6)',
  },
  headerAccent: {
    fontSize: 18,
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  headerAccentLux: {
    fontSize: 20,
    filter: 'drop-shadow(0 0 8px rgba(232,208,138,0.6))',
  },
  title: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: '-0.04em',
    background: `linear-gradient(135deg, ${GOLD2} 0%, ${GOLD} 50%, #a0783a 100%)`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    margin: 0,
  },
  titleLux: {
    fontSize: 28,
    background: `linear-gradient(135deg, ${GOLD2} 0%, #fff5d0 35%, ${GOLD} 70%, #a0783a 100%)`,
    filter: 'drop-shadow(0 2px 8px rgba(201,168,76,0.35))',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 11,
    fontWeight: 500,
    color: TEXT3,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  subtitleLux: {
    color: 'rgba(232,208,138,0.55)',
    letterSpacing: '0.14em',
  },
  divider: {
    height: 1,
    background: `linear-gradient(90deg, transparent, ${BORDER_GOLD}, transparent)`,
    margin: '0',
  },
  dividerLux: {
    height: 1,
    background: `linear-gradient(90deg, transparent, rgba(232,208,138,0.5), rgba(160,120,200,0.35), transparent)`,
    boxShadow: '0 0 12px rgba(201,168,76,0.2)',
  },
  sectionRow: {
    margin: '24px 20px 6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  sectionPremiumLink: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: '6px 10px',
    borderRadius: 999,
    border: '1px solid rgba(160,120,200,0.4)',
    background: 'rgba(123,92,184,0.18)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 800,
    color: 'rgba(220,200,255,0.95)',
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
  },
  sectionLabel: {
    margin: '24px 20px 6px',
    fontSize: 10,
    fontWeight: 800,
    color: TEXT3,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sectionLabelLux: {
    color: 'rgba(232,208,138,0.65)',
    letterSpacing: '0.14em',
  },
  sectionCrown: {
    color: GOLD2,
    fontWeight: 400,
    filter: 'drop-shadow(0 0 6px rgba(232,208,138,0.7))',
  },
  sectionHint: {
    margin: '0 20px 10px',
    fontSize: 12,
    color: TEXT2,
    lineHeight: 1.45,
    fontWeight: 400,
  },
  sectionHintLux: {
    color: 'rgba(232,208,138,0.7)',
  },
  sectionDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
    flexShrink: 0,
    boxShadow: `0 0 8px rgba(201,168,76,0.6)`,
    display: 'inline-block',
  },
  sectionDotLux: {
    width: 7,
    height: 7,
    boxShadow: '0 0 14px rgba(232,208,138,0.8)',
  },
  list: {
    padding: '0 16px 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 14px',
    borderRadius: 16,
    border: `1px solid ${BORDER_GOLD}`,
    background: SURFACE,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    boxShadow: '0 2px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(201,168,76,0.08)',
    transition: 'transform 0.14s ease, box-shadow 0.14s ease',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
  },
  cardPremium: {
    border: '1px solid rgba(232,208,138,0.35)',
    boxShadow: '0 4px 24px rgba(201,168,76,0.15), inset 0 1px 0 rgba(255,255,255,0.1)',
  },
  cardPremiumLux: {
    border: '1px solid rgba(232,208,138,0.5)',
    boxShadow: '0 6px 28px rgba(201,168,76,0.22), 0 0 40px rgba(160,120,200,0.12), inset 0 1px 0 rgba(255,255,255,0.14)',
  },
  cardLocked: {
    opacity: 0.88,
    border: '1px solid rgba(160,120,200,0.35)',
  },
  cardDemo: {
    border: '1px solid rgba(255,255,255,0.07)',
    background: 'rgba(255,255,255,0.025)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
  },
  cardDemoPremium: {
    border: '1px solid rgba(201,168,76,0.25)',
    background: 'rgba(201,168,76,0.06)',
    boxShadow: '0 2px 16px rgba(201,168,76,0.1)',
  },
  glyphWrap: {
    width: 46,
    height: 46,
    borderRadius: 13,
    background: `linear-gradient(135deg, rgba(201,168,76,0.25), rgba(232,208,138,0.12))`,
    border: `1px solid rgba(201,168,76,0.3)`,
    color: GOLD2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: `0 0 12px rgba(201,168,76,0.15), inset 0 1px 0 rgba(255,255,255,0.08)`,
  },
  glyphWrapPremium: {
    background: 'linear-gradient(135deg, rgba(201,168,76,0.4), rgba(232,208,138,0.2))',
    border: '1px solid rgba(232,208,138,0.45)',
    boxShadow: '0 0 16px rgba(201,168,76,0.25), inset 0 1px 0 rgba(255,255,255,0.12)',
  },
  glyphWrapPremiumLux: {
    background: 'linear-gradient(135deg, rgba(232,208,138,0.45) 0%, rgba(160,120,200,0.3) 100%)',
    border: '1px solid rgba(255,245,210,0.5)',
    boxShadow: '0 0 20px rgba(232,208,138,0.35), inset 0 1px 0 rgba(255,255,255,0.18)',
    color: '#fff8e8',
  },
  glyphWrapLocked: {
    background: 'linear-gradient(135deg, rgba(100,80,140,0.35), rgba(60,40,90,0.2))',
    border: '1px solid rgba(160,120,200,0.4)',
    color: 'rgba(200,180,230,0.9)',
    boxShadow: 'none',
  },
  glyphWrapDemo: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.09)',
    color: TEXT3,
    boxShadow: 'none',
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: '-0.025em',
    color: TEXT,
  },
  badgeLock: {
    fontSize: 11,
    fontWeight: 700,
    color: 'rgba(200,170,240,0.95)',
    letterSpacing: '0.02em',
  },
  badgeTimer: {
    fontSize: 10, fontWeight: 700,
    color: 'rgba(232,208,138,0.85)',
    letterSpacing: '0.02em',
  },
  badgeVip: {
    fontSize: 10,
    fontWeight: 800,
    color: GOLD2,
    letterSpacing: '0.04em',
    textShadow: '0 0 8px rgba(232,208,138,0.6)',
  },
  cardDesc: {
    fontSize: 12,
    color: TEXT2,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  chevron: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: `rgba(201,168,76,0.45)`,
  },
  chevronLux: {
    color: 'rgba(232,208,138,0.75)',
    filter: 'drop-shadow(0 0 4px rgba(232,208,138,0.4))',
  },
  hint: {
    margin: '20px 20px 0',
    fontSize: 11,
    color: TEXT3,
    lineHeight: 1.6,
    fontWeight: 400,
    fontStyle: 'italic',
  },
  hintLux: {
    color: 'rgba(232,208,138,0.45)',
  },
  hintRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    margin: '0 20px 10px',
  },
  createBtn: {
    width: 30, height: 30, borderRadius: '50%',
    background: 'rgba(201,168,76,0.18)',
    border: '1px solid rgba(201,168,76,0.45)',
    color: GOLD2, fontSize: 20, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
  },
  hostBlock: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 5, flexShrink: 0, width: 46,
  },
  officialAvatar: {
    width: 46, height: 46, borderRadius: 13, flexShrink: 0,
    background: `linear-gradient(135deg, rgba(201,168,76,0.35), rgba(232,208,138,0.15))`,
    border: `1px solid rgba(201,168,76,0.4)`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 14px rgba(201,168,76,0.2)',
  },
  officialAvatarEmoji: {
    fontSize: 18,
    background: `linear-gradient(135deg, ${GOLD2}, ${GOLD})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  userAvatar: {
    width: 46, height: 46, borderRadius: 13, objectFit: 'cover',
    border: `1px solid rgba(201,168,76,0.3)`,
    flexShrink: 0,
  },
  userAvatarFallback: {
    width: 46, height: 46, borderRadius: 13, flexShrink: 0,
    background: `linear-gradient(135deg, rgba(201,168,76,0.3), rgba(232,208,138,0.15))`,
    border: `1px solid rgba(201,168,76,0.3)`,
    color: GOLD2, fontSize: 18, fontWeight: 800,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  hostBlockName: {
    fontSize: 9, color: TEXT3, fontWeight: 600,
    letterSpacing: '0.04em', textAlign: 'center',
    maxWidth: 46, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  deleteRoomBtn: {
    background: 'none', border: 'none',
    color: 'rgba(255,100,100,0.7)', fontSize: 18,
    cursor: 'pointer', padding: '0 4px', flexShrink: 0,
  },
  reportRoomBtn: {
    background: 'none', border: 'none',
    color: 'rgba(232,208,138,0.75)', fontSize: 15,
    cursor: 'pointer', padding: '0 4px', flexShrink: 0,
    lineHeight: 1,
  },
  modalBackdrop: {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  },
  modal: {
    background: '#fff', color: '#20242b', border: 0,
    borderRadius: '12px 12px 0 0',
    padding: '0 0 calc(20px + env(safe-area-inset-bottom))',
    width: '100%', maxWidth: 'var(--app-max-w)', maxHeight: '85dvh', overflowY: 'auto',
    display: 'flex', flexDirection: 'column',
  },
  modalHeader: {
    display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8,
    padding: '8px 16px', borderBottom: '1px solid #eee',
  },
  modalTitle: { fontSize: 15, fontWeight: 600, color: '#20242b' },
  modalCancelBtn: {
    justifySelf: 'start', minHeight: 44, background: 'none', border: 0,
    color: '#737780', fontSize: 13, fontWeight: 400,
    fontFamily: 'inherit', cursor: 'pointer', padding: '8px 0',
  },
  modalPostBtn: {
    justifySelf: 'end', minWidth: 44, minHeight: 44, padding: '8px 0', borderRadius: 0, border: 0,
    background: 'transparent', color: '#1674c4', fontSize: 14, fontWeight: 600,
    fontFamily: 'inherit', cursor: 'pointer',
  },
  modalCompose: { display: 'flex', padding: '20px 20px 8px', alignItems: 'flex-start' },
  modalFields: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  modalTitleInput: {
    background: '#fff', border: 0, borderRadius: 0,
    color: '#20242b', fontSize: 16, fontWeight: 400,
    fontFamily: 'inherit', padding: '12px 0', minHeight: 44, width: '100%',
  },
  modalDividerLine: { height: 1, background: '#eee', margin: '4px 0' },
  modalDescInput: {
    background: '#fff', border: 0, borderRadius: 0,
    color: '#20242b', fontSize: 16, fontFamily: 'inherit',
    padding: '12px 0', minHeight: 44, width: '100%',
  },
  modalError: { margin: '0 20px', fontSize: 13, color: '#b42318' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', padding: '8px 20px 0' },
  modalCount: { fontSize: 11, color: '#96999e' },
};
